import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Trash2, Shield, Eye, Edit3, Loader2, Users, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CollaboratorsPanel({ eventId, isOwner, isPro }) {
  const [collaborators, setCollaborators] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);

  const token = localStorage.getItem('session_token');

  const fetchCollaborators = useCallback(async () => {
    if (!isOwner || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/collaborators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCollaborators(data.collaborators || []);
        setPendingInvites(data.pending_invites || []);
      }
    } catch (err) {
      console.error('Failed to fetch collaborators', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, isOwner, token]);

  useEffect(() => { fetchCollaborators(); }, [fetchCollaborators]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/collaborators`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), role: inviteRole })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setInviteEmail('');
        fetchCollaborators();
      } else {
        toast.error(data.detail || 'Failed to invite');
      }
    } catch (err) {
      toast.error('Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId, email) => {
    const label = email || 'this collaborator';
    if (!window.confirm(`Remove ${label} from this event? They will lose access immediately.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/collaborators/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Collaborator removed');
        fetchCollaborators();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to remove');
      }
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleRevokeInvite = async (inviteId, email) => {
    if (!window.confirm(`Revoke the pending invitation for ${email}?`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/${inviteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Invitation revoked');
        fetchCollaborators();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to revoke invitation');
      }
    } catch (err) {
      toast.error('Failed to revoke invitation');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/collaborators/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success(`Role updated to ${newRole}`);
        fetchCollaborators();
      }
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  if (!isOwner) return null;
  if (!isPro) {
    return (
      <Card className="border-slate-200" data-testid="collaborators-panel">
        <CardContent className="py-6 text-center">
          <Shield className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Multi-admin collaboration is a Pro feature</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => window.location.href = '/pricing'}>
            Upgrade to Pro
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="collaborators-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <Users className="w-4 h-4 text-[#00247D]" />
          COLLABORATORS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Invite Form */}
        <div className="flex gap-2">
          <Input data-testid="invite-email-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email address" className="h-9 flex-1"
            onKeyPress={(e) => e.key === 'Enter' && handleInvite()} />
          <Select value={inviteRole} onValueChange={setInviteRole}>
            <SelectTrigger className="h-9 w-28" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="editor"><span className="flex items-center gap-1"><Edit3 className="w-3 h-3" />Editor</span></SelectItem>
              <SelectItem value="viewer"><span className="flex items-center gap-1"><Eye className="w-3 h-3" />Viewer</span></SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9 bg-[#00247D] hover:bg-[#001A5C]" onClick={handleInvite} disabled={inviting}
            data-testid="invite-btn">
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-2">
            {/* Active Collaborators */}
            {collaborators.map((c) => (
              <div key={c.user_id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg"
                data-testid={`collaborator-${c.email}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#00247D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {(c.name || c.email)?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-700 truncate block">{c.name || c.email}</span>
                    {c.name && <span className="text-xs text-slate-400 truncate block">{c.email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={c.role} onValueChange={(val) => handleRoleChange(c.user_id, val)}>
                    <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                    onClick={() => handleRemove(c.user_id, c.email)}
                    data-testid={`remove-collaborator-${c.email}`}
                    title="Remove collaborator"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Pending Invites */}
            {pendingInvites.map((inv) => (
              <div key={inv.invite_id} className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg gap-2"
                data-testid={`pending-invite-${inv.email}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">?</div>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-700 truncate block">{inv.email}</span>
                    <Badge variant="outline" className="mt-0.5 text-[10px] text-amber-600 border-amber-300">Pending</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-slate-500 capitalize hidden sm:inline">{inv.role}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-amber-800 hover:text-rose-700 hover:bg-rose-50 font-semibold"
                    onClick={() => handleRevokeInvite(inv.invite_id, inv.email)}
                    data-testid={`revoke-invite-${inv.email}`}
                    title="Revoke invitation"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                  </Button>
                </div>
              </div>
            ))}

            {collaborators.length === 0 && pendingInvites.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-3">No collaborators yet. Invite team members to help manage this event.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
